import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getRepetitiveTaskManagerLogs,
  getTaskManagerLogs,
} from "../../store/slices/taskmanagementSlice";
import { IChange } from "../../models/taskManagement.model";
import { IUserRef } from "../../models/userData.model";

interface TaskManagerLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  isRepetitive?: boolean;
}

const TaskManagerLogsModal: React.FC<TaskManagerLogsModalProps> = ({
  isOpen,
  onClose,
  taskId,
  isRepetitive = false,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { taskManagementLogs, repetitiveTaskManagementLogs, logsLoading } =
    useSelector((state: RootState) => state.TaskManagement);

  const logs = isRepetitive ? repetitiveTaskManagementLogs : taskManagementLogs;

  useEffect(() => {
    if (isOpen && taskId) {
      if (isRepetitive) {
        dispatch(getRepetitiveTaskManagerLogs({ taskId }));
      } else {
        dispatch(getTaskManagerLogs({ taskId }));
      }
    }
  }, [isOpen, taskId, isRepetitive, dispatch]);

  if (!isOpen) return null;

  const getChangeIcon = (field: string) => {
    const iconProps = "w-4 h-4";

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={iconProps}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    );
  };

  const formatChangeMessage = (change: IChange, taskName?: string) => {
    const formatValue = (val: any) => {
      // If it's a date string or Date object, format it
      if (val && (val instanceof Date || !isNaN(Date.parse(val)))) {
        return new Date(val).toISOString().split("T")[0]; // YYYY-MM-DD
      }

      // If it's an object with username
      if (val && typeof val === "object" && val.username) {
        return val.username;
      }
      // If it's an attachment object

      if (typeof val === "object" && val?.name) {
        return val.name;
      }

      // If it's an array of usernames
      if (Array.isArray(val) && val.length > 0 && val[0]?.username) {
        return val.map((v) => v.username).join(", ");
      }

      // Default fallback
      return String(val ?? "");
    };

    if (change.field === "requestedEndDate") {
      if (change.newValue === null || change.newValue === "") {
        return `Request of ${taskName} end date extension for "${formatValue(
          change.oldValue
        )}" has been REJECTED`;
      } else {
        return `Request of ${taskName} end date extension for "${formatValue(
          change.newValue
        )}"`;
      }
    }

    if (change.field === "endDate" && change.oldValue && change.newValue) {
      return `Request of ${taskName} end date extension APPROVED: updated from "${formatValue(
        change.oldValue
      )}" to "${formatValue(change.newValue)}"`;
    }

    if (change.field === "status") {
      const oldStatus = formatValue(change.oldValue).toLowerCase();
      const newStatus = formatValue(change.newValue).toLowerCase();

      // Waiting for approval → Duplicate
      if (oldStatus === "waiting for approval" && newStatus === "duplicate") {
        return `Request for duplication of "${taskName}" has been approved.`;
      }

      // Waiting for approval → Completed
      if (oldStatus === "waiting for approval" && newStatus === "completed") {
        return `Request for completion of "${taskName}" has been approved.`;
      }

      if (
        oldStatus === "waiting for approval" &&
        newStatus === "ongoing" &&
        change.reason === "duplicate"
      ) {
        return `Request for duplication of "${taskName}" has been rejected.`;
      }

      // Completion rejected
      if (
        oldStatus === "waiting for approval" &&
        newStatus === "ongoing" &&
        change.reason === "completion"
      ) {
        return `Request for completion of "${taskName}" has been rejected.`;
      }

      // Fallback for other status changes
      return `Status changed from "${formatValue(
        change.oldValue
      )}" to "${formatValue(change.newValue)}"`;
    }

    if (change.field === "typeOfRepetition") {
      const parseRepetition = (repetition: any) => {
        if (!repetition || typeof repetition !== "object") return null;

        try {
          let repetitionObj = repetition;
          if (typeof repetition === "string") {
            repetitionObj = JSON.parse(repetition);
          }
          return repetitionObj;
        } catch (e) {
          return null;
        }
      };

      const oldRepetition = parseRepetition(change.oldValue);
      const newRepetition = parseRepetition(change.newValue);

      if (!oldRepetition || !newRepetition) {
        return `Repetition pattern changed from "${formatValue(
          change.oldValue
        )}" to "${formatValue(change.newValue)}"`;
      }

      // Check if only noOfDays changed
      const oldNoOfDays = oldRepetition.data?.noOfDays;
      const newNoOfDays = newRepetition.data?.noOfDays;
      const oldType = oldRepetition.type;
      const newType = newRepetition.type;

      // If only noOfDays changed
      if (
        oldType === newType &&
        JSON.stringify({
          ...oldRepetition,
          data: { ...oldRepetition.data, noOfDays: undefined },
        }) ===
          JSON.stringify({
            ...newRepetition,
            data: { ...newRepetition.data, noOfDays: undefined },
          })
      ) {
        return `Number of days changed from ${oldNoOfDays || 0} to ${
          newNoOfDays || 0
        }`;
      }

      // Format the repetition for display
      const formatRepetitionDisplay = (repetition: any) => {
        const { type, data } = repetition;

        switch (type) {
          case "weekly":
            return `Weekly on ${data.days?.join(", ") || "no days"}${
              data.noOfDays ? ` (${data.noOfDays} days)` : ""
            }`;
          case "monthly":
            const monthlyDate = data.date
              ? new Date(data.date).getDate()
              : "unknown date";
            return `Monthly on day ${monthlyDate}${
              data.noOfDays ? ` (${data.noOfDays} days)` : ""
            }`;
          case "yearly":
            const yearlyDate = data.date
              ? new Date(data.date).toLocaleDateString()
              : "unknown date";
            return `Yearly on ${yearlyDate}${
              data.noOfDays ? ` (${data.noOfDays} days)` : ""
            }`;
          default:
            return `Repetition type: ${type}${
              data.noOfDays ? ` (${data.noOfDays} days)` : ""
            }`;
        }
      };

      const oldDisplay = formatRepetitionDisplay(oldRepetition);
      const newDisplay = formatRepetitionDisplay(newRepetition);

      return `Repetition pattern changed from "${oldDisplay}" to "${newDisplay}"`;
    }
    // Handle followers changes specifically
    if (change.field === "followers") {
      const oldFollowers = Array.isArray(change.oldValue)
        ? change.oldValue
        : [];
      const newFollowers = Array.isArray(change.newValue)
        ? change.newValue
        : [];

      // added followers
      const addedFollowers = newFollowers.filter(
        (follower: IUserRef) =>
          !oldFollowers.some((old: IUserRef) => old._id === follower._id)
      );

      // removed followers
      const removedFollowers = oldFollowers.filter(
        (follower: IUserRef) =>
          !newFollowers.some(
            (newFollower: IUserRef) => newFollower._id === follower._id
          )
      );

      if (addedFollowers.length > 0 && removedFollowers.length > 0) {
        return `Followers updated: Added ${addedFollowers
          .map((f: IUserRef) => f.username)
          .join(", ")} | Removed ${removedFollowers
          .map((f: IUserRef) => f.username)
          .join(", ")}`;
      } else if (addedFollowers.length > 0) {
        return `Added followers: ${addedFollowers
          .map((f: IUserRef) => f.username)
          .join(", ")}`;
      } else if (removedFollowers.length > 0) {
        return `Removed followers: ${removedFollowers
          .map((f: IUserRef) => f.username)
          .join(", ")}`;
      }
    }

    if (change.field === "attachments") {
      const oldFiles = Array.isArray(change.oldValue) ? change.oldValue : [];
      const newFiles = Array.isArray(change.newValue) ? change.newValue : [];

      const removedFiles = oldFiles.filter(
        (f: any) => !newFiles.some((nf: any) => nf._id === f._id)
      );

      if (removedFiles.length) {
        return `Removed attachments: ${removedFiles
          .map((f: any) => f.name)
          .join(", ")}`;
      }
    }

    // Default handling for other fields
    return `${change.field} changed from "${formatValue(
      change.oldValue
    )}" to "${formatValue(change.newValue)}"`;
  };

  const getChangeTypeStyles = (field: string) => {
    const styles: Record<string, string> = {
      default: "bg-gray-100 border border-gray-300 text-gray-700",
    };
    return styles[field] || styles.default;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-slate-100 to-gray-200 px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Task Activity Logs
              </h2>
              <p className="text-blue-600 text-xs mt-1">
                Track all task changes and updates
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-700 hover:bg-gray-400 hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 group"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 group-hover:rotate-90 transition-transform duration-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {logsLoading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 mt-4 font-medium">
                Loading activity logs...
              </p>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="bg-gray-100 rounded-full p-6 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                No Activity Logs Available
              </h3>
              <p className="text-gray-500 text-xs text-center max-w-md">
                No changes have been made to this task yet. Updates and
                modifications will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex gap-2">
                  <div className="flex space-x-2">
                    <div className="bg-blue-100 rounded-full p-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-xs">
                        Total Updates:
                      </h3>
                      <p className="text-xs text-gray-600">Activity history</p>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-blue-600">
                    {logs.length}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 to-gray-200"></div>

                {logs.map((log, idx) => {
                  const isLatest = idx === 0;

                  return (
                    <div key={log._id} className="relative mb-8 last:mb-0">
                      <div
                        className={`absolute left-6 w-5 h-5 rounded-full border-4 bg-white ${
                          isLatest
                            ? "border-blue-500 shadow-lg shadow-blue-200"
                            : "border-gray-300"
                        }`}
                      >
                        {isLatest && (
                          <div className="absolute inset-1 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                      </div>

                      <div className="ml-16">
                        <div
                          className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 ${
                            isLatest
                              ? "border-blue-200 bg-blue-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="px-4 pt-4 border-b border-gray-100">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-semibold text-xs ${
                                    isLatest ? "bg-blue-500" : "bg-gray-400"
                                  }`}
                                >
                                  {(log.updatedBy?.username || "System")
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-800 text-xs">
                                    {log.updatedBy?.username || "System"}
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    {new Date(
                                      log.createdAt!
                                    ).toLocaleDateString("en-US", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {log.changes.length}{" "}
                                  {log.changes.length === 1
                                    ? "change"
                                    : "changes"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4">
                            <div className="space-y-3">
                              {log.changes.map(
                                (change: IChange, changeIdx: number) => (
                                  <div
                                    key={changeIdx}
                                    className={`p-2 rounded-lg ${getChangeTypeStyles(
                                      change.field
                                    )}`}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className="flex-shrink-0 mt-0.5">
                                        {getChangeIcon(change.field)}
                                      </div>
                                      <p className="text-xs font-medium leading-relaxed">
                                        {formatChangeMessage(
                                          change,
                                          log.taskId.taskName
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskManagerLogsModal;
